// Which tank a new HYDROS key goes to, and what to say once it is added.
// Feedback #69 (2026-10-12): a keeper with a freshwater and a saltwater
// aquarium, one tank in TankSensei and a key for each. The form sent every
// key to the only tank without asking, the second controller was kept
// unlinked because that tank already read the first, and the only way
// offered was to switch. Nothing said the second aquarium needs a tank.

export const NEW_TANK = 'new';

const list = (v) => (Array.isArray(v) ? v : []);

const DEAD_KEY = new Set(['key_rejected', 'device_gone']);

// Controllers already read through a working key: a new key is for another
// one. A controller read by password, or whose key HYDROS turned down, is the
// one a new key most likely replaces, so it does not count.
export function keyedControllers(keys) {
  return new Set(
    list(keys)
      .filter((k) => k.device_id && !DEAD_KEY.has(k.status))
      .map((k) => k.device_id)
  );
}

// The tank picked without asking (owner, 2026-09-30: the only tank), unless
// that tank already reads a controller with its own key.
export function presetKeyTank(tanks, keyed = new Set()) {
  const own = list(tanks);
  return own.length === 1 && !keyed.has(own[0].hydros_thing_id) ? own[0].id : '';
}

export function asksKeyTank(tanks, keyed = new Set(), otherAquarium = false) {
  const own = list(tanks);
  if (!own.length) return false;
  return own.length > 1 || otherAquarium || keyed.has(own[0].hydros_thing_id);
}

// names: thing id -> controller name, from the account's controller list.
export function keyTankOptions(tanks, names = new Map()) {
  return [
    ...list(tanks).map((t) => {
      const reads = t.hydros_thing_id ? names.get(t.hydros_thing_id) : null;
      return {
        value: t.id,
        label: reads
          ? `${t.name} (reads ${reads} now)`
          : t.hydros_thing_id
            ? `${t.name} (reads another controller now)`
            : t.name,
      };
    }),
    { value: NEW_TANK, label: 'A tank not in TankSensei yet' },
  ];
}

// What to say once a controller is linked to a tank from Your controllers
// (out: the link answer). Readings wait for a probe pick when the
// controller feeds another tank too, or carries two probes of one kind.
export function linkedNote(tankName, controllerName, out) {
  if (list(out?.needs_choice).length) {
    return `${tankName} is linked to ${controllerName}. Pick which probes are ${tankName}'s: open ${tankName}'s Settings, Equipment, HYDROS controller. Readings start once you pick.`;
  }
  return `${tankName} now reads ${controllerName}. The first readings show up within a few minutes.`;
}

// { text, addTank }: addTank shows an "Add a tank" button with the text.
// tanks null means the keeper's tanks had not loaded: nothing is assumed.
export function keyAddedMessage(out, { tanks, choice } = {}) {
  const name = out?.key?.device_name || 'Your controller';
  const tankName = out?.linked_tank?.name;
  const kept = out?.tank_kept_controller;
  const onTanks = list(out?.on_tanks);
  if (!tankName && onTanks.length) {
    return {
      text: `${name} is connected. ${onTanks.map((t) => t.name).join(', ')} already ${
        onTanks.length > 1 ? 'read' : 'reads'
      } it.`,
      addTank: false,
    };
  }
  if (kept) {
    return {
      text: `${name} is connected. ${kept.name} still reads ${kept.current_name}. If ${name} runs another aquarium, add a tank for it and link ${name} to that tank below. To switch ${kept.name} to ${name} instead, link it below.`,
      addTank: true,
    };
  }
  if (!tankName) {
    if (Array.isArray(tanks) && !tanks.length) {
      return {
        text: `${name} is connected. Add a tank first, then link the controller to it here.`,
        addTank: true,
      };
    }
    if (choice === NEW_TANK) {
      return {
        text: `${name} is connected. Add the tank it runs, then link ${name} to it below.`,
        addTank: true,
      };
    }
    return { text: `${name} is connected. Pick the tank it belongs to below.`, addTank: false };
  }
  if (out.needs_choice?.length) {
    return {
      text: `${name} is connected to ${tankName}. It carries probes for more than one tank, so pick which are ${tankName}'s: open ${tankName}'s Settings, Equipment, HYDROS controller. Readings start once you pick.`,
      addTank: false,
    };
  }
  return {
    text: `${name} is connected to ${tankName}. The first readings show up within a few minutes.`,
    addTank: false,
  };
}
